import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "relative min-h-[360px]" }
const _hoisted_2 = {
  key: 0,
  class: "absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/80"
}
const _hoisted_3 = { class: "flex items-center gap-2 text-sm text-gray-700" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", null, "Loading activity logs...", -1)
const _hoisted_5 = { class: "p-6" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("h3", { class: "text-lg font-semibold text-gray-900" }, "Activity Log Metadata", -1)
const _hoisted_7 = { class: "text-sm text-gray-500 mt-1 mb-4" }
const _hoisted_8 = { class: "rounded-lg border border-gray-200 bg-gray-50 p-4 max-h-[60vh] overflow-auto" }
const _hoisted_9 = { class: "text-xs text-gray-800 whitespace-pre-wrap break-words" }

import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useFilterStore } from "@/stores/filterStore";
import { useUserStore } from "@/stores/userStore";
import { useActivityLogStore } from "@/stores/activityLogStore";
import ActivityLogsFiltersComponent from "@/modules/main/components/internal-tools/activity-logs/ActivityLogsFiltersComponent.vue";
import ActivityLogsTableComponent from "@/modules/main/components/internal-tools/activity-logs/ActivityLogsTableComponent.vue";
import { ActivityLog, ActivityLogFilterState } from "@/interfaces/activity-log.interface";


export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityLogsInternalView',
  setup(__props) {

const filterStore = useFilterStore();
const userStore = useUserStore();
const activityLogStore = useActivityLogStore();
const router = useRouter();
const route = useRoute();
const initialPageLoading = ref(true);
const metadataModalOpen = ref(false);
const selectedLog = ref<ActivityLog | null>(null);

const formattedMetadataJson = computed(() => {
  const metadata = selectedLog.value?.details;
  return JSON.stringify(metadata ?? {}, null, 2);
});

const initialiseForSchool = async () => {
  initialPageLoading.value = true;

  try {
    const selectedSchoolId = filterStore.selectedFilters.school;
    activityLogStore.setSelectedSchool(selectedSchoolId || "");
    await activityLogStore.fetchAdminOptionsBySchool();
    await activityLogStore.fetchLogs(true);
  } finally {
    initialPageLoading.value = false;
  }
};

const handleFilterUpdate = ({
  key,
  value,
}: {
  key: keyof ActivityLogFilterState;
  value: string | string[] | boolean;
}) => {
  activityLogStore.setFilters({ [key]: value } as Partial<ActivityLogFilterState>);
};

const applyFilters = async () => {
  await activityLogStore.fetchLogs(true);
};

const showMore = async () => {
  await activityLogStore.showMore();
};

const openLogMetadataModal = (log: ActivityLog) => {
  selectedLog.value = log;
  metadataModalOpen.value = true;
};

const applyAdminPresetFromQuery = () => {
  const rawAdminId = route.query.adminId;
  const adminId = typeof rawAdminId === "string" ? rawAdminId : "";
  if (!adminId) return false;

  const rawAllSchools = route.query.allSchools;
  const includeAllSchoolsForAdmin =
    rawAllSchools === "1" || rawAllSchools === "true";

  activityLogStore.setFilters({
    adminIds: [adminId],
    actionTypes: [],
    entityTypes: [],
    startDate: "",
    endDate: "",
    includeAllSchoolsForAdmin,
  });

  return true;
};

onMounted(async () => {
  if (!userStore.isInternalAdmin) {
    router.push("/main/admin");
    return;
  }

  const hasPreset = applyAdminPresetFromQuery();

  await initialiseForSchool();

  if (hasPreset) {
    // Clear the one-shot preset params from the URL but keep the tab so a
    // refresh stays on the Activity Logs view.
    await router.replace({ path: route.path, query: { tab: "ActivityLogs" } });
  }
});

watch(
  () => filterStore.selectedFilters.school,
  async (newSchoolId, oldSchoolId) => {
    if (!userStore.isInternalAdmin) {
      return;
    }

    if (!newSchoolId || newSchoolId === oldSchoolId) {
      return;
    }

    activityLogStore.setSelectedSchool(newSchoolId);
    if (!activityLogStore.filters.includeAllSchoolsForAdmin) {
      activityLogStore.setFilters({ adminIds: [] });
    }
    await activityLogStore.fetchAdminOptionsBySchool();
    await activityLogStore.fetchLogs(true);
  }
);

return (_ctx: any,_cache: any) => {
  const _component_spinner_loader = _resolveComponent("spinner-loader")!
  const _component_modal = _resolveComponent("modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (initialPageLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_spinner_loader, { color: 'text-blue-600 dark:text-blue-600' }),
            _hoisted_4
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", null, [
      _createVNode(ActivityLogsFiltersComponent, {
        filters: _unref(activityLogStore).filters,
        adminOptions: _unref(activityLogStore).adminOptions,
        actionTypeOptions: _unref(activityLogStore).actionTypeOptions,
        entityTypeOptions: _unref(activityLogStore).entityTypeOptions,
        loading: _unref(activityLogStore).loading,
        onUpdateFilters: handleFilterUpdate,
        onApplyFilters: applyFilters
      }, null, 8, ["filters", "adminOptions", "actionTypeOptions", "entityTypeOptions", "loading"]),
      _createVNode(ActivityLogsTableComponent, {
        logs: _unref(activityLogStore).logs,
        loading: _unref(activityLogStore).loading,
        loadingMore: _unref(activityLogStore).loadingMore,
        hasMore: _unref(activityLogStore).pagination.hasMore,
        total: _unref(activityLogStore).pagination.total,
        onShowMore: showMore,
        onSelectLog: openLogMetadataModal
      }, null, 8, ["logs", "loading", "loadingMore", "hasMore", "total"]),
      _createVNode(_component_modal, {
        showCloseButton: true,
        widthClass: 'max-w-3xl',
        open: metadataModalOpen.value,
        onLocalModalOpen: _cache[0] || (_cache[0] = ($event: any) => (metadataModalOpen.value = $event))
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_5, [
            _hoisted_6,
            _createElementVNode("p", _hoisted_7, _toDisplayString(selectedLog.value?.actionType || '-') + " · " + _toDisplayString(selectedLog.value?.entityType || '-'), 1),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("pre", _hoisted_9, _toDisplayString(formattedMetadataJson.value), 1)
            ])
          ])
        ]),
        _: 1
      }, 8, ["open"])
    ])
  ]))
}
}

})